import { FlaskConical } from 'lucide-react'
import type { PoolPreview } from '@/types/products'
import {
  formatPoolValue,
  limsLabelFor,
  missingWeightMessage,
  orderPoolRows,
  orderedWarnings,
  poolMemberSummaries,
  poolUnavailableMessage,
  warningIsBlocking,
} from '@/lib/poolPreview'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { cn } from '@/lib/utils'

/**
 * The pool's arithmetic, shown before it runs.
 *
 * The mirror of `SplitLimsPreview`, and for the same reason: z8nrz7dkvf asks
 * for the recalculation to be VISIBLE rather than taken on trust. What each
 * bag contributes, what the pooled bag comes out at, and the formula that
 * turns one into the other. Collapsed by default, because an operator pooling
 * two bags does not need twenty rows in their way, and it is the place a
 * reviewer goes when a dose looks wrong.
 *
 * Every figure and every formula string comes from the server
 * (`POST /products/pool/preview`), produced by the same pass that will store
 * the results. Nothing here computes a dose, and nothing here should start to.
 */
export function PoolLimsPreview({
  preview,
  isLoading,
  error,
}: {
  preview: PoolPreview | undefined
  isLoading: boolean
  error: Error | null
}) {
  const warnings = preview ? orderedWarnings(preview.warnings, preview.unavailable) : []
  const weightNote = preview ? missingWeightMessage(preview.missingForWeight) : null
  const noteCount =
    (preview?.unavailable ? 1 : 0) + warnings.length + (weightNote ? 1 : 0)

  const members = preview ? poolMemberSummaries(preview.members) : []
  const rows = preview ? orderPoolRows(preview.rows) : []

  return (
    <Accordion type='single' collapsible className='rounded-lg border'>
      <AccordionItem value='lims' className='border-b-0'>
        <AccordionTrigger className='px-3 py-2.5 text-sm hover:no-underline'>
          <span className='flex items-center gap-2'>
            <FlaskConical className='h-4 w-4 text-muted-foreground' />
            How the LIMS results are recalculated
            {noteCount > 0 && (
              <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-900'>
                {noteCount === 1 ? '1 note' : `${noteCount} notes`}
              </span>
            )}
          </span>
        </AccordionTrigger>

        <AccordionContent className='space-y-3 px-3'>
          {isLoading && <p className='text-sm text-muted-foreground'>Calculating…</p>}

          {error && (
            <p className='text-sm text-destructive'>
              Could not calculate the pooled results: {error.message}
            </p>
          )}

          {preview?.unavailable && (
            <p className='text-sm text-muted-foreground'>
              {poolUnavailableMessage(preview.unavailable)}
            </p>
          )}

          {warnings.map(w => (
            <p
              key={w.code}
              className={cn(
                'rounded-md px-2.5 py-2 text-sm',
                warningIsBlocking(w)
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-amber-50 text-amber-900',
              )}
            >
              {w.message}
            </p>
          ))}

          {weightNote && <p className='text-sm text-muted-foreground'>{weightNote}</p>}

          {members.length > 0 && (
            <div>
              <p className='mb-1.5 text-xs font-medium text-muted-foreground'>
                What each bag contributes
              </p>
              <div className='overflow-x-persistent rounded-md border'>
                <table className='w-full text-sm'>
                  <thead className='bg-muted/50 text-xs text-muted-foreground'>
                    <tr>
                      <th className='px-2.5 py-1.5 text-left font-medium'>Product</th>
                      <th className='px-2.5 py-1.5 text-right font-medium'>Volume</th>
                      <th className='px-2.5 py-1.5 text-right font-medium'>TNC (×10⁶)</th>
                      <th className='px-2.5 py-1.5 text-right font-medium'>CD34 (×10⁶)</th>
                      <th className='px-2.5 py-1.5 text-right font-medium'>CD3 (×10⁸)</th>
                      <th className='px-2.5 py-1.5 text-left font-medium'>Measured by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={i} className='border-t'>
                        <td className='px-2.5 py-1.5 font-mono text-xs'>{m.label}</td>
                        <td className='px-2.5 py-1.5 text-right tabular-nums'>{m.volume}</td>
                        <td className='px-2.5 py-1.5 text-right tabular-nums'>{m.tnc}</td>
                        <td className='px-2.5 py-1.5 text-right tabular-nums'>{m.cd34}</td>
                        <td className='px-2.5 py-1.5 text-right tabular-nums'>{m.cd3}</td>
                        <td className='px-2.5 py-1.5 text-xs text-muted-foreground'>{m.rule}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {rows.length > 0 && preview && (
            <div>
              <p className='mb-1.5 text-xs font-medium text-muted-foreground'>
                The pooled product — {formatPoolValue(preview.volumeMl)} mL
                {preview.bufferMl > 0 && ` (including ${formatPoolValue(preview.bufferMl)} mL buffer)`}
              </p>
              <div className='overflow-x-persistent rounded-md border'>
                <table className='w-full text-sm'>
                  <thead className='bg-muted/50 text-xs text-muted-foreground'>
                    <tr>
                      <th className='px-2.5 py-1.5 text-left font-medium'>Result</th>
                      <th className='px-2.5 py-1.5 text-right font-medium'>Pooled</th>
                      <th className='px-2.5 py-1.5 text-left font-medium'>How</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.key} className='border-t align-top'>
                        <td className='px-2.5 py-1.5'>{limsLabelFor(r)}</td>
                        <td className='px-2.5 py-1.5 text-right tabular-nums'>
                          {formatPoolValue(r.after)}
                        </td>
                        <td className='px-2.5 py-1.5 text-xs text-muted-foreground'>
                          <span className='block'>{r.formula}</span>
                          <span className='block font-mono'>{r.substituted}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
